// adapters.ts — convert DRF response shapes (paginated envelopes, snake_case
// fields) into the UI domain shapes that the rest of the app uses. Each
// adapter is one small pure function so it's easy to follow what changes.

import type { Product, Bin, Batch, Allocation, Store, StoreProduct } from '@/domain/types';
import { ukDateTime } from './stocksAdapters';

interface DrfPage<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function unwrapPage<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const page = payload as DrfPage<T>;
  return page?.results ?? [];
}

// --- Catalog → Product ---

interface CatalogItemDto {
  id: string;
  name: string;
  dmd_code: string;
  dosage_form: string;
  unit: string;
  manufacturer: string;
  is_controlled_drug: boolean;
  is_active: boolean;
}

export function catalogItemToProduct(c: unknown): Product {
  const d = c as CatalogItemDto;
  return {
    id: d.id,
    sku: d.dmd_code || d.id.slice(0, 8),
    name: d.name,
    strength: '',
    form: d.dosage_form,
    pack: '',
    uom: d.unit,
    barcode: '',
    mfr: d.manufacturer,
    nhs: d.dmd_code,
    cat: '',
    storage: 'Ambient 15–25°C',
    cd: d.is_controlled_drug,
    fridge: false,
    min: 0,
    max: 0,
    status: d.is_active ? 'Active' : 'Archived',
  };
}

// --- ProductSnapshot (dm+d + C+D combined) → Product ---

interface ProductSnapshotDto {
  id: string;
  snapshot_key: string;
  display_name: string;
  legal_category: string;
  legal_category_display: string;
  cd_schedule: string;
  cd_schedule_display?: string;
  cd_cabinet?: boolean;
  cd_register?: boolean;
  is_active: boolean;
  reorder_min?: number | null;
  reorder_max?: number | null;
  source: string;
  level: string;
  manufacturer: string;
  category: string;
  barcode: string;
  pack_size: string;
  is_special_container: boolean;
  dose_count: string | null;
  is_fridge: boolean;
  dosage_form: string;
  ampp_code: string;
  pip_code: string;
  brand_name: string;
  vmp_name: string;
  vmpp_id: string;
  family_vmpp_id?: string;
  vmp_id?: string;
  has_children: boolean | null;
  created_at: string;
}

export function snapshotToProduct(d: unknown): Product {
  const dto = d as ProductSnapshotDto;
  const isCD = dto.cd_schedule !== '0000' && dto.cd_schedule !== '';
  // Use AMPP code or PIP code as SKU; strip version prefix from snapshot_key as last resort
  const snapshotCode = dto.snapshot_key.includes(':')
    ? dto.snapshot_key.split(':').slice(1).join(':')
    : dto.snapshot_key;
  const sku = dto.ampp_code || dto.pip_code || snapshotCode;
  return {
    id: dto.id,
    sku,
    name: dto.display_name,
    strength: '',
    form: dto.dosage_form || '',
    pack: dto.pack_size,
    isSpecialContainer: dto.is_special_container,
    doseCount: dto.dose_count ? Number(dto.dose_count) : null,
    uom: '',
    barcode: dto.barcode,
    mfr: dto.manufacturer,
    nhs: dto.ampp_code || snapshotCode,
    cat: dto.category,
    storage: dto.is_fridge ? 'Cold 2–8°C' : 'Ambient 15–25°C',
    cd: isCD,
    fridge: dto.is_fridge,
    // Per-product reorder levels from the tenant/store overlay (null = no override yet → 0).
    min: dto.reorder_min ?? 0,
    max: dto.reorder_max ?? 0,
    status: dto.is_active ? 'Active' : 'Archived',
    // Enhanced fields
    legalCategory: dto.legal_category,
    legalCategoryDisplay: dto.legal_category_display,
    cdSchedule: dto.cd_schedule,
    cdScheduleDisplay: dto.cd_schedule_display ?? '',
    cdCabinet: dto.cd_cabinet ?? false,
    cdRegister: dto.cd_register ?? false,
    source: dto.source,
    level: dto.level,
    amppCode: dto.ampp_code,
    pipCode: dto.pip_code,
    brandName: dto.brand_name,
    vmpName: dto.vmp_name,
    vmppId: dto.vmpp_id,
    familyVmppId: dto.family_vmpp_id ?? '',
    vmpId: dto.vmp_id,
    hasChildren: dto.has_children ?? null,
    snapshotKey: dto.snapshot_key,
  };
}

// --- Store Product (aggregated inventory product) ---

interface StoreProductDto {
  product_id: string;
  sku: string;
  name: string;
  form: string;
  strength: string;
  pack_size: string;
  category: string;
  legal_category: string;
  storage: string;
  is_controlled: boolean;
  is_fridge: boolean;
  is_active: boolean;
  total_qty: number;
  total_allocated: number;
  total_free: number;
  total_blocked: number;
  total_packs?: number;
  free_packs?: number;
  alloc_packs?: number;
  batch_count: number;
  bin_count: number;
  earliest_expiry: string | null;
  expiring_soon_count: number;
  status: string;
  level?: string;
  maker_count?: number;
  manufacturers?: Array<{
    product_id: string;
    sku: string;
    name: string;
    manufacturer: string;
    pack_size?: string;
    pack_qty?: number | null;
    vmpp_code?: string;
    vmpp_name?: string;
    total_qty: number;
    pack_count?: number;
    loose_units?: number;
    is_special_container?: boolean;
    batch_count: number;
  }>;
  batches: Array<{
    id: string;
    batch_number: string;
    expiry_date: string;
    qty_on_hand: number;
    qty_allocated: number;
    qty_free: number;
    qty_blocked: number;
    bin_location: string;
    status: string;
    sku?: string;
    manufacturer?: string;
  }>;
  bins: Array<{
    bin_location: string;
    qty: number;
    batch_count: number;
  }>;
}

export function storeProductDtoToStoreProduct(d: unknown): StoreProduct {
  const dto = d as StoreProductDto;
  return {
    productId: dto.product_id,
    sku: dto.sku,
    name: dto.name,
    form: dto.form,
    strength: dto.strength,
    packSize: dto.pack_size,
    category: dto.category,
    legalCategory: dto.legal_category,
    storage: dto.storage,
    isControlled: dto.is_controlled,
    isFridge: dto.is_fridge,
    isActive: dto.is_active,
    totalQty: dto.total_qty,
    totalAllocated: dto.total_allocated,
    totalFree: dto.total_free,
    totalBlocked: dto.total_blocked,
    totalPacks: dto.total_packs ?? 0,
    freePacks: dto.free_packs ?? 0,
    allocPacks: dto.alloc_packs ?? 0,
    batchCount: dto.batch_count,
    binCount: dto.bin_count,
    earliestExpiry: dto.earliest_expiry,
    expiringSoonCount: dto.expiring_soon_count,
    status: dto.status as StoreProduct['status'],
    level: dto.level,
    makerCount: dto.maker_count,
    manufacturers: (dto.manufacturers || []).map((m) => ({
      productId: m.product_id,
      sku: m.sku,
      name: m.name,
      manufacturer: m.manufacturer,
      packSize: m.pack_size,
      packQty: m.pack_qty,
      vmppCode: m.vmpp_code,
      vmppName: m.vmpp_name,
      totalQty: m.total_qty,
      packCount: m.pack_count,
      looseUnits: m.loose_units,
      isSpecialContainer: m.is_special_container,
      batchCount: m.batch_count,
    })),
    batches: (dto.batches || []).map((b) => ({
      id: b.id,
      batchNumber: b.batch_number,
      expiryDate: b.expiry_date,
      qtyOnHand: b.qty_on_hand,
      qtyAllocated: b.qty_allocated,
      qtyFree: b.qty_free,
      qtyBlocked: b.qty_blocked,
      binLocation: b.bin_location,
      status: b.status,
      sku: b.sku,
      manufacturer: b.manufacturer,
    })),
    bins: (dto.bins || []).map((bin) => ({
      binLocation: bin.bin_location,
      qty: bin.qty,
      batchCount: bin.batch_count,
    })),
  };
}

// --- StorageBin → Bin ---

interface StorageBinDto {
  id: string;
  tenant_id: string;
  store_id: string;
  code: string;
  label: string;
  physical_location?: string;
  bin_type: string;
  storage_condition: string;
  capacity?: number;
  is_active: boolean;
  occupied_qty?: number;
  suggested_capacity?: number | null;
}

export function storageBinToBin(d: unknown): Bin {
  const dto = d as StorageBinDto;
  return {
    id: dto.id,
    code: dto.code,
    label: dto.label,
    physicalLocation: dto.physical_location ?? '',
    store: dto.store_id,
    storeName: '',
    type: dto.bin_type,
    cap: dto.capacity ?? 0,
    temp: dto.storage_condition,
    occupiedQty: dto.occupied_qty ?? 0,
    suggestedCapacity: dto.suggested_capacity ?? null,
  };
}

// --- BatchStock → Batch ---

interface OpenPackDto {
  id: string;
  original_qty: number;
  remaining_qty: number;
  reserved_qty: number;
  free_qty: number;
  state: string;
}

interface PackBreakdownDto {
  units_per_pack: number;
  full_packs: number;
  loose_units: number;
  open_pack_count: number;
  open_units: number;
  open_packs: OpenPackDto[];
}

interface BatchStockDto {
  id: string;
  tenant_id: string;
  store_id: string;
  sku: string;
  /** Concrete maker pack (AMPP) on a VMPP-keyed batch; empty for a directly-keyed batch. */
  ampp_snapshot_key?: string;
  display_name?: string;
  product_snapshot?: string;
  batch_number: string;
  expiry_date: string;
  expiry_is_default?: boolean;
  qty_on_hand: number;
  qty_free_hold: number;
  qty_allocated: number;
  qty_blocked: number;
  bin_location: string;
  units_per_pack?: number | null;
  pack_breakdown?: PackBreakdownDto;
  is_excess?: boolean;
  excess_note?: string;
  version: number;
  created_at: string;
  updated_at?: string;
}

export function batchStockToBatch(d: unknown): Batch {
  const dto = d as BatchStockDto;
  const pb = dto.pack_breakdown;
  return {
    id: dto.id,
    batchNo: dto.batch_number,
    // Attribute the batch to its CONCRETE maker pack (AMPP) when the stock is keyed at the shared
    // VMPP virtual-pack level (VMPP-centric direct import) — mirrors the overview/store-products
    // aggregation (ampp_snapshot_key || sku), so a product's batches show on its own detail page
    // instead of vanishing under the family key.
    product: dto.ampp_snapshot_key || dto.sku,
    displayName: dto.display_name || '',
    snapshotId: dto.product_snapshot || '',
    store: dto.store_id,
    bin: dto.bin_location || '',
    qty: dto.qty_on_hand,
    free: dto.qty_free_hold,
    alloc: dto.qty_allocated,
    exp: dto.expiry_date,
    expiryIsDefault: dto.expiry_is_default ?? false,
    isExcess: dto.is_excess ?? false,
    excessNote: dto.excess_note ?? '',
    status: dto.qty_blocked > 0 ? 'Hold' : 'Active',
    last: ukDateTime(dto.updated_at || dto.created_at),
    hold: dto.qty_blocked > 0 ? 'Blocked' : null,
    unitsPerPack: dto.units_per_pack ?? undefined,
    packBreakdown: pb
      ? {
          unitsPerPack: pb.units_per_pack,
          fullPacks: pb.full_packs,
          looseUnits: pb.loose_units,
          openPackCount: pb.open_pack_count,
          openUnits: pb.open_units,
          openPacks: (pb.open_packs ?? []).map((o) => ({
            id: o.id,
            original: o.original_qty,
            remaining: o.remaining_qty,
            reserved: o.reserved_qty,
            free: o.free_qty,
            state: o.state,
          })),
        }
      : undefined,
  };
}

// --- StockAllocation → Allocation ---

interface StockAllocationDto {
  id: string;
  tenant_id: string;
  store_id: string;
  batch_stock: string;
  qty: number;
  reference_type: string;
  reference_id: string;
  status: string;
  hold_expires_at: string | null;
  release_type: string | null;
  released_at: string | null;
  created_at: string;
}

export function stockAllocationToAllocation(d: unknown): Allocation {
  const dto = d as StockAllocationDto;
  return {
    id: dto.id,
    rx: dto.reference_id,
    product: '',
    batch: dto.batch_stock,
    qty: dto.qty,
    status:
      dto.status === 'dispensed'
        ? 'Dispensed'
        : dto.status === 'released'
          ? 'Released'
          : 'Allocated',
    by: '',
    date: dto.created_at,
  };
}

// --- Store (identity) → Store ---

interface StoreDto {
  id: string;
  tenant: string;
  name: string;
  code: string;
  store_type: string;
  address: string;
  is_active: boolean;
}

export function storeDtoToStore(d: unknown): Store {
  const dto = d as StoreDto;
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    storeType: dto.store_type,
    address: dto.address,
    active: dto.is_active,
  };
}

// --- DispensingStation (for stations list) ---

export type DispenseLocationType = 'general' | 'shelf' | 'fridge' | 'cd_cabinet' | 'returns';

export interface Station {
  id: string;
  store: string;
  code: string;
  label: string;
  location_type: DispenseLocationType;
  barcode_value: string;
  capacity: number;
  max_hold_minutes: number | null;
  display_order: number;
  is_active: boolean;
}

interface DispensingStationDto {
  id: string;
  tenant_id: string;
  store_id: string;
  code: string;
  label: string;
  location_type?: DispenseLocationType;
  barcode_value?: string;
  capacity?: number;
  max_hold_minutes?: number | null;
  display_order?: number;
  is_active: boolean;
}

export function stationDtoToStation(d: unknown): Station {
  const dto = d as DispensingStationDto;
  return {
    id: dto.id,
    store: dto.store_id,
    code: dto.code,
    label: dto.label,
    location_type: dto.location_type ?? 'general',
    barcode_value: dto.barcode_value ?? dto.code,
    capacity: dto.capacity ?? 0,
    max_hold_minutes: dto.max_hold_minutes ?? null,
    display_order: dto.display_order ?? 0,
    is_active: dto.is_active,
  };
}

// --- InventoryPolicy ---

export interface Policy {
  id: string;
  tenant_id: string;
  store_id: string | null;
  name: string;
  fefo_enforced: boolean;
  expiry_alert_days: number;
  expiry_alert_combine: boolean;
  allocation_hold_minutes: number;
  variance_unit_tolerance: number;
  variance_percent_tolerance: string;
  variance_absolute_ceiling: number;
  cd_variance_unit_tolerance: number;
  usage_window_days: number;
  low_usage_days: number;
  dead_stock_days: number;
  stock_level_mode: 'absolute' | 'percentage';
  low_stock_threshold: number;
  reorder_point: number;
  max_stock_level: number;
  auto_default_batch_enabled: boolean;
  default_batch_prefix: string;
  default_expiry_months: number;
}

interface PolicyDto {
  id: string;
  tenant_id: string;
  store_id: string | null;
  name: string;
  fefo_enforced: boolean;
  expiry_alert_days: number;
  expiry_alert_combine: boolean;
  allocation_hold_minutes: number;
  variance_unit_tolerance: number;
  variance_percent_tolerance: string;
  variance_absolute_ceiling: number;
  cd_variance_unit_tolerance: number;
  usage_window_days: number;
  low_usage_days: number;
  dead_stock_days: number;
  stock_level_mode: 'absolute' | 'percentage';
  low_stock_threshold: number;
  reorder_point: number;
  max_stock_level: number;
  auto_default_batch_enabled: boolean;
  default_batch_prefix: string;
  default_expiry_months: number;
}

export function policyDtoToPolicy(d: unknown): Policy {
  return d as PolicyDto;
}

// --- BulkImportJob ---

export interface BulkJob {
  id: string;
  job_type: string;
  status: string;
  total_rows: number;
  success_rows: number;
  error_rows: number;
  error_details: unknown;
  created_at: string;
}

interface BulkJobDto {
  id: string;
  tenant_id: string;
  store_id: string | null;
  job_type: string;
  status: string;
  total_rows: number;
  success_rows: number;
  error_rows: number;
  error_details: unknown;
  created_at: string;
}

export function bulkJobDtoToJob(d: unknown): BulkJob {
  const dto = d as BulkJobDto;
  return {
    id: dto.id,
    job_type: dto.job_type,
    status: dto.status,
    total_rows: dto.total_rows,
    success_rows: dto.success_rows,
    error_rows: dto.error_rows,
    error_details: dto.error_details,
    created_at: dto.created_at,
  };
}

// --- Product → CatalogItemDto (for POST/PATCH to backend) ---

export function productToCatalogPayload(p: Partial<Product>): Record<string, unknown> {
  return {
    name: p.name,
    dmd_code: p.nhs || p.sku || '',
    dosage_form: p.form || '',
    unit: p.uom || '',
    manufacturer: p.mfr || '',
    is_controlled_drug: p.cd ?? false,
    is_active: p.status !== 'Archived',
  };
}

// --- Bin → StorageBinDto (for POST/PATCH to backend) ---

export function binToStorageBinPayload(b: Partial<Bin>, storeId: string): Record<string, unknown> {
  return {
    store_id: b.store || storeId,
    code: b.code || '',
    label: b.label || b.code || b.type || '',
    physical_location: b.physicalLocation || '',
    bin_type: b.type || 'general',
    storage_condition: b.temp || 'ambient',
    capacity: b.cap ?? 0,
    is_active: true,
  };
}
